import { Box, Menu, MenuItem } from '@mui/material';
import {
  isCategoryAvailable,
  seedFactorOf,
  setCategoryFactor,
  setCategoryHidden,
  upliftIsVisible,
} from '@timelog/pm-shared';
import { useState } from 'react';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useFinancials } from './editor';

import { captureEvent } from '@/analytics/posthogClient';
import { MaterialSymbol } from '@/components/MaterialSymbol';

/**
 * BR-9: hidden categories re-enter through this tile, starting from 0 — but
 * only the ones the budget-model config makes available (BR-27): an
 * unavailable category is never offered, whatever the contract holds.
 *
 * A section whose uplift is hidden comes back at 1.0 (CALC-25, ADR 0046): the
 * factor it was seeded with before is replaced, in the same revision as the
 * unhide, so the document says what the section operates at. A visible
 * section keeps its stored factor — that one is the user's number.
 */
export const AddCategoryTile = () => {
  const { state, dispatch, budgetModel } = useFinancials();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const { t } = useTranslation(['pmFinancials', 'pmCommon']);

  const hidden = state.document.categories.filter(
    (category) => category.hidden && isCategoryAvailable(budgetModel.config, category.id),
  );
  if (hidden.length === 0) return null;

  return (
    <>
      <Box
        component="button"
        type="button"
        data-automation-id="add-category"
        onClick={(event: MouseEvent<HTMLElement>) => setAnchor(event.currentTarget)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          border: 'var(--border-width-thin) dashed var(--border-divider)',
          borderRadius: 'var(--border-radius-x-large)',
          bgcolor: 'transparent',
          color: 'var(--fg-3)',
          minHeight: 76,
          fontSize: 11,
          fontFamily: 'var(--font-family)',
          cursor: 'pointer',
          '&:hover': {
            borderColor: 'var(--border-subtle)',
            color: 'var(--fg-2)',
            bgcolor: 'var(--bg-sunken)',
          },
        }}
      >
        <MaterialSymbol name="add" size={20} />
        {t('addCategory.Label')}
      </Box>
      <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
        {hidden.map((category) => (
          <MenuItem
            key={category.id}
            data-automation-id="add-category-option"
            onClick={() => {
              setAnchor(null);
              if (upliftIsVisible(budgetModel.config, category.id)) {
                dispatch({ type: 'SET_CATEGORY_HIDDEN', id: category.id, hidden: false });
              } else {
                const factor = seedFactorOf(budgetModel.config, category.id);
                dispatch({
                  type: 'APPLY_OP',
                  op: (document) =>
                    setCategoryFactor(
                      setCategoryHidden(document, category.id, false),
                      category.id,
                      factor,
                    ),
                });
              }
              captureEvent('category_added', { category: category.id });
            }}
          >
            {t(`pmCommon:category.${category.id}`)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
